import { motion } from "framer-motion";
import {
  Search,
  ClipboardList,
  PenTool,
  Code2,
  Rocket,
  LifeBuoy,
} from "lucide-react";

import { Container } from "@/components/common/Container";
import { Section } from "@/components/common/Section";
import { SectionHeading } from "@/components/common/SectionHeading";

import {
  staggerContainer,
  fadeUp,
  viewportOnce,
} from "@/animations/motion";

const processSteps = [
  {
    step: "01",
    title: "Discovery & Consultation",
    icon: Search,
    description:
      "We begin by understanding your organisation, business processes, operational challenges and project goals through collaborative workshops and discussions.",
  },
  {
    step: "02",
    title: "Solution Planning",
    icon: ClipboardList,
    description:
      "Our team analyse your requirements, identify opportunities for improvement, define the project scope and implementation strategy to ensure successful delivery.",
  },
  {
    step: "03",
    title: "UI / UX Design",
    icon: PenTool,
    description:
      "Our team designs intuitive user experiences, scalable system architecture and secure database structures tailored to your business needs.",
  },
  {
    step: "04",
    title: "Development & Integration",
    icon: Code2,
    description:
      "We develop, test and integrate with your existing systems using modern technologies while keeping you involved through regular demonstrations and feedback.",
  },
  {
    step: "05",
    title: "Testing & Quality Assurance",
    icon: Rocket,
    description:
      "Every feature is thoroughly tested for functionality, security, performance and usability before deployment to ensure a reliable production release.",
  },
  {
    step: "06",
    title: "Deployment & Continuous Support",
    icon: LifeBuoy,
    description:
      "After successful deployment, we provide training, monitoring, maintenance and continuous improvements to ensure long-term success.",
  },
];

export function ProcessTimeline() {
  return (
    <Section id="process">
      <Container>

        <SectionHeading
          eyebrow="Our Delivery Process"
          title="A Proven Software Delivery Process"
          description="Every successful digital solution starts with a structured process. We work collaboratively from discovery through deployment and long-term support."
        />

        <motion.div
          variants={staggerContainer(0.08, 0.10)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="relative mt-16 grid gap-8 md:grid-cols-2 xl:grid-cols-3"
        >
          {processSteps.map((item) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.step}
                variants={fadeUp}
                className="group relative overflow-hidden rounded-3xl border border-border bg-card p-8 transition-all duration-300 hover:-translate-y-2 hover:border-primary/20 hover:shadow-xl"
              >
                <div className="absolute right-6 top-5 text-6xl font-bold text-primary/10">
                  {item.step}
                </div>

                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon size={28} />
                </div>

                <h3 className="mt-6 text-2xl font-semibold">
                  {item.title}
                </h3>

                <p className="mt-4 leading-7 text-muted-foreground">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

      </Container>
    </Section>
  );
}